<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <h1 class="text-h3 mb-4">Initiate Return Request</h1>

        <!-- Loading State -->
        <v-card v-if="loading" class="pa-4 text-center">
          <v-progress-circular
            indeterminate
            color="primary"
            size="64"
          ></v-progress-circular>
          <div class="text-h6 mt-4">Loading sale details...</div>
        </v-card>

        <!-- Error State -->
        <v-alert
          v-else-if="error"
          type="error"
          class="mb-4"
        >
          {{ error }}
        </v-alert>

        <!-- Success State -->
        <template v-else-if="submitted">
          <v-card class="mb-4">
            <v-card-text class="text-center">
              <v-icon
                icon="mdi-check-circle"
                color="success"
                size="64"
              ></v-icon>
              <div class="text-h5 mt-4">
                Return has been initiated for sale {{ sale.ID }} successfully!
              </div>
              <div class="mt-6">
                <v-btn
                  color="primary"
                  :to="sale.Vendor === 'eBay' ? `/returns/ebay` : `/returns`"
                  class="mr-2"
                >
                  View All Returns
                </v-btn>
                <v-btn
                  :to="`/sales/${sale.ID}`"
                  variant="text"
                >
                  Back to Sale
                </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </template>

        <template v-if="!submitted && !loading">
          <!-- Sale Details Card -->
          <v-card class="mb-4">
            <v-card-title>Sale Details</v-card-title>
            <v-card-text>
              <v-row>
                <v-col cols="12" md="4">
                  <div class="text-caption text-medium-emphasis">Order Time</div>
                  <div>{{ formatDateTime(sale.CreatedDate) }}</div>
                </v-col>
                <v-col cols="12" md="4">
                  <div class="text-caption text-medium-emphasis">PO Number</div>
                  <div>{{ sale.PONumber }}</div>
                </v-col>
                <v-col cols="12" md="4">
                  <div class="text-caption text-medium-emphasis">Site</div>
                  <div>{{ sale.Site }}</div>
                </v-col>
                <v-col cols="12" md="4">
                  <div class="text-caption text-medium-emphasis">Vendor</div>
                  <div>{{ sale.Vendor }}</div>
                </v-col>
                <v-col cols="12" md="4">
                  <div class="text-caption text-medium-emphasis">Buyer ID</div>
                  <div>{{ sale.BuyerID }}</div>
                </v-col>
                <v-col cols="12" md="4">
                  <div class="text-caption text-medium-emphasis">Shipping Name</div>
                  <div>{{ sale.shippingname }}</div>
                </v-col>
                <v-col cols="12" md="12" class="text-primary font-weight-bold">
                  <div class="text-caption text-medium-emphasis">Part Number</div>
                  <div>{{ sale.itemnumber }}</div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Return Form -->
          <v-card>
            <v-card-title>Return Details</v-card-title>
            <v-card-text>
              <v-form @submit.prevent="handleSubmit">
                <v-row>
                  <v-col cols="12">
                    <v-text-field
                      v-model="formData.ReturnReason"
                      label="Return Reason"
                    ></v-text-field>
                  </v-col>


                  <v-col cols="8" md="4">
                    <v-text-field
                      v-model="formData.TrackingNumber"
                      label="Tracking Number"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="4" md="2">
                    <v-select
                      v-model="formData.TrackingService"
                      label="Tracking Carrier"
                      :items="['', 'UPS', 'USPS', 'FedEx']"
                    ></v-select>
                  </v-col>

                  <v-col cols="12">
                    <v-text-field
                      v-model="formData.InternalNotes"
                      label="Internal Notes"
                      required
                      :rules="[v => !!v || 'Internal Notes are required']"
                      rows="3"
                    />
                  </v-col>

                  <v-col cols="12" class="d-flex justify-end">
                    <v-btn
                      type="submit"
                      color="primary"
                      size="large"
                      :disabled="!formData.InternalNotes"
                      :loading="submitting"
                    >
                      Submit Return Request
                    </v-btn>
                  </v-col>
                </v-row>
              </v-form>
            </v-card-text>
          </v-card>
        </template>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { salesService } from '@/services/salesService'
import { returnsService } from '@/services/returnsService'

const route = useRoute()

// State
const sale = ref({})
const loading = ref(false)
const error = ref(null)
const submitting = ref(false)
const submitted = ref(false)

const formData = ref({
  ReturnReason: '',
  TrackingNumber: '',
  TrackingService: '',
  InternalNotes: ''
})

// Fetch sale details
const fetchSale = async () => {
  loading.value = true
  error.value = null

  try {
    const response = await salesService.getSale(route.params.saleId)
    sale.value = response
  } catch (err) {
    console.error('Error fetching sale:', err)
    error.value = 'Failed to load sale details'
  } finally {
    loading.value = false
  }
}

// Handle form submission
const handleSubmit = async () => {
  submitting.value = true
  error.value = null

  try {
    await returnsService.initiateReturn(route.params.saleId, formData.value)
    submitted.value = true
  } catch (err) {
    console.error('Error submitting return:', err)
    error.value = 'Failed to submit return request ' + err.response?.data?.message
  } finally {
    submitting.value = false
  }
}

// Initialize
onMounted(() => {
  fetchSale()
})

// Format date and time helper
const formatDateTime = (date) => {
  if (!date) return ''
  return new Date(date).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  })
}
</script> 